import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Radio, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", team_name: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register({
          name: form.name,
          email: form.email,
          password: form.password,
          team_name: form.team_name || undefined,
        });
      }
      toast.success("Sessão iniciada");
      navigate("/copilot");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => setForm({ ...form, email: "admin@pitchpulse.ai", password: "admin123" });

  return (
    <div className="min-h-screen grain-bg flex flex-col lg:flex-row">
      {/* Left brand panel */}
      <div className="lg:w-1/2 relative flex flex-col justify-between p-8 sm:p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1542473079-b8419a8853c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F17] via-[#0B0F17]/85 to-[#0B0F17]/60" />

        <div className="relative flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-[#2563EB] flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" fill="white" />
          </div>
          <span className="font-display font-extrabold text-xl">PitchPulse AI</span>
        </div>

        <div className="relative max-w-md">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
            O seu copiloto em cada chamada de vendas.
          </h1>
          <p className="mt-5 text-[#94A3B8] text-base leading-relaxed">
            Guiões dinâmicos, deteção de objeções em tempo real e respostas geradas por IA que
            aprendem com a sua equipa. Feche mais negócios no Google Meet e no Zoom.
          </p>
          <div className="mt-8 space-y-3">
            {[
              { icon: Radio, t: "Deteção de objeções ao vivo (voz + transcrição)" },
              { icon: Sparkles, t: "Respostas em 3 camadas geradas por Claude IA" },
              { icon: ShieldCheck, t: "Biblioteca de objeções que melhora com o tempo" },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex items-center gap-3 text-sm text-[#F8FAFC]">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#2563EB]" />
                  </div>
                  {f.t}
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative text-xs text-[#64748B]">© 2026 PitchPulse AI</div>
      </div>

      {/* Right form */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex gap-1 p-1 bg-[#111827] rounded-xl border border-white/10 mb-8">
            <button
              onClick={() => setMode("login")}
              data-testid="auth-tab-login"
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                mode === "login" ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"
              }`}
            >
              Entrar
            </button>
            <button
              onClick={() => setMode("register")}
              data-testid="auth-tab-register"
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                mode === "register" ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"
              }`}
            >
              Criar conta
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-[#94A3B8]">Nome</Label>
                <Input
                  data-testid="register-name-input"
                  value={form.name}
                  onChange={set("name")}
                  required
                  placeholder="O seu nome"
                  className="mt-1.5 bg-[#111827] border-white/10 h-11"
                />
              </div>
            )}
            <div>
              <Label className="text-[#94A3B8]">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                placeholder="voce@empresa.com"
                className="mt-1.5 bg-[#111827] border-white/10 h-11"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Palavra-passe</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={form.password}
                onChange={set("password")}
                required
                placeholder="••••••••"
                className="mt-1.5 bg-[#111827] border-white/10 h-11"
              />
            </div>
            {mode === "register" && (
              <div>
                <Label className="text-[#94A3B8]">Nome da equipa (opcional)</Label>
                <Input
                  data-testid="register-team-input"
                  value={form.team_name}
                  onChange={set("team_name")}
                  placeholder="Ex: Vendas Ibéria"
                  className="mt-1.5 bg-[#111827] border-white/10 h-11"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold"
            >
              {loading ? "A processar…" : mode === "login" ? "Entrar" : "Criar conta e equipa"}
            </Button>
          </form>

          {mode === "login" && (
            <button
              onClick={fillDemo}
              data-testid="fill-demo-button"
              className="mt-4 w-full text-center text-xs text-[#64748B] hover:text-[#2563EB] transition-colors"
            >
              Usar conta demo (admin@pitchpulse.ai / admin123)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
